// staff.js — unified theme for all staff-facing surfaces.
//
// Goal: give the backend the same warmth and intention as the public /site
// preview. Navy base, brand accents, Fredoka display font. One module so we
// can re-tune the entire backend by editing this file.
//
// How to use inside a view:
//   import { STAFF_THEME, FONT } from "./theme/staff";
//   const BRAND = STAFF_THEME;
// Existing `BRAND.dark / panel / card / raised / etc.` references continue to
// work because the keys match.
//
// Functional state colors (success/warning/danger/state-indicators) live with
// their views — those carry meaning and shouldn't be retuned as aesthetics.

export const STAFF_THEME = {
  // ── Surface stack (navy, progressively lighter for elevation) ─────────────
  bg:      "#0d1833",   // page background — deepest
  panel:   "#142548",   // top bars, tab strips, section panels
  card:    "#1a3068",   // cards, inputs, list rows
  raised:  "#234080",   // selected / hovered / modal surfaces

  // Legacy alias — many views use `BRAND.dark`. Same value as bg so there's
  // no visual difference after the swap.
  dark:    "#0d1833",

  // ── Brand accents (match /site + existing CSS vars) ───────────────────────
  blue:    "#1f59db",
  navy:    "#1d52a0",
  sky:     "#4aa4e1",
  gold:    "#fcb31f",
  pink:    "#ef537e",
  cream:   "#ffdaad",
  orange:  "#f57600",

  // ── Text ──────────────────────────────────────────────────────────────────
  text:    "#ffffff",
  muted:   "rgba(255,255,255,0.65)",
  dim:     "rgba(255,255,255,0.4)",

  // ── Lines ─────────────────────────────────────────────────────────────────
  // Borders use a low-opacity sky tint so they read against the navy stack
  // without fighting brand color accents.
  border:  "rgba(74,164,225,0.28)",
  divider: "rgba(74,164,225,0.14)",

  // ── Functional / utility ──────────────────────────────────────────────────
  // Kept in the theme for convenience. Their meaning is fixed — don't retune
  // these for aesthetics, only for accessibility (contrast) if ever needed.
  green:   "#70be44",
  red:     "#e74c3c",
  warning: "#f39c12",
};

// Typography. Fredoka is loaded from Google Fonts in public/index.html;
// Lobster Two likewise. Body AND display both resolve to Fredoka so the
// whole staff app shares the warmth of the main menu. Semantic distinction
// preserved (display vs body) so we can diverge later if we want a different
// body face — e.g., a humanist sans — without chasing every call site.
// Lobster Two stays rare on purpose: reserved for top-level page titles
// (main menu, Meditations) where "handwritten" earns its place.
export const FONT = {
  display: "'Fredoka', system-ui, -apple-system, 'Segoe UI', sans-serif",
  script:  "'Lobster Two', 'Fredoka', cursive",
  body:    "'Fredoka', system-ui, -apple-system, 'Segoe UI', sans-serif",
};

// Deep-maroon to navy substitution map. Use in file-local replacements for
// inline hex colors that bypass BRAND (modals, alert strips, HUD chrome).
// Values chosen to preserve the contrast relationships the old palette had.
export const MAROON_TO_NAVY = {
  "#1a0a12": STAFF_THEME.bg,       // deepest — page bg
  "#160810": "#0a1230",            // slightly darker panel accent (HUD strips)
  "#100616": "#0a1230",
  "#181818": STAFF_THEME.panel,    // panel elevation
  "#0a0a0a": "#060c20",            // deepest footer/strip bg
};
